import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface UserCoinBalance {
  id: string;
  user_name: string;
  balance: number;
  last_transfer_at: string | null;
  created_at: string;
  updated_at: string;
}

export function useUserCoinBalance(userName: string) {
  return useQuery({
    queryKey: ['user-coin-balance', userName],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_coin_balances')
        .select('*')
        .eq('user_name', userName)
        .single();

      if (error) {
        // If not found, return a default balance
        if (error.code === 'PGRST116') {
          return { user_name: userName, balance: 0 } as UserCoinBalance;
        }
        throw error;
      }
      return data as UserCoinBalance;
    },
  });
}

export function useUpdateCoinBalance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userName, delta }: { userName: string; delta: number }) => {
      // First, try to get current balance
      const { data: current, error: fetchError } = await supabase
        .from('user_coin_balances')
        .select('balance')
        .eq('user_name', userName)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }

      const currentBalance = current?.balance || 0;
      const newBalance = Math.max(0, currentBalance + delta);

      if (current) {
        // Update existing
        const { error } = await supabase
          .from('user_coin_balances')
          .update({ balance: newBalance, updated_at: new Date().toISOString() })
          .eq('user_name', userName);
        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          .from('user_coin_balances')
          .insert({ user_name: userName, balance: newBalance });
        if (error) throw error;
      }

      return newBalance;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user-coin-balance', variables.userName] });
    },
  });
}

export function useTransferCoins() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userName: string) => {
      // First get the current balance to log
      const { data: current } = await supabase
        .from('user_coin_balances')
        .select('balance')
        .eq('user_name', userName)
        .single();

      const amount = current?.balance || 0;
      
      if (amount > 0) {
        // Log the transfer to history
        const { error: historyError } = await supabase
          .from('coin_transfer_history')
          .insert({ user_name: userName, amount });
        
        if (historyError) throw historyError;
      }

      // Reset balance to 0
      const { error } = await supabase
        .from('user_coin_balances')
        .update({ 
          balance: 0, 
          last_transfer_at: new Date().toISOString(),
          updated_at: new Date().toISOString() 
        })
        .eq('user_name', userName);

      if (error) throw error;
    },
    onSuccess: (_, userName) => {
      queryClient.invalidateQueries({ queryKey: ['user-coin-balance', userName] });
      queryClient.invalidateQueries({ queryKey: ['coin-transfer-history', userName] });
    },
  });
}

interface CoinTransfer {
  id: string;
  user_name: string;
  amount: number;
  transferred_at: string;
}

export function useCoinTransferHistory(userName: string) {
  return useQuery({
    queryKey: ['coin-transfer-history', userName],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('coin_transfer_history')
        .select('*')
        .eq('user_name', userName)
        .order('transferred_at', { ascending: false });

      if (error) throw error;
      return data as CoinTransfer[];
    },
  });
}
